import { useEffect, useMemo, useState } from 'react'
import { Box, Checkbox, Stack, Typography } from '@mui/material'
import { Button, Modal } from '@repo/ui'

import { useSnackbar } from 'contexts'
import { useVerafiedActions } from 'hooks'
import type { VerafiedEpisode } from 'services'

import { ToneChip } from '../../InternalAnalyticsPage/components/shared'
import { industryLabel, solutionLabel } from './verafiedModel'

/**
 * VRG-734 spec §5 — review the model's suggested solutions and industries and apply them in bulk.
 * Every row starts selected; untick the ones that are wrong, then apply or dismiss the selection.
 * Applying fills only an episode's EMPTY lists, so nothing typed by hand is overwritten (server).
 * Row layout copies AssignUsersModal (working-conventions §28).
 */
export const SuggestionsReviewModal = ({ open, onClose, episodes }: { open: boolean; onClose: () => void; episodes: VerafiedEpisode[] }) => {
  const { open: notify } = useSnackbar()
  const { applySuggestions, dismissSuggestions } = useVerafiedActions()
  const pending = useMemo(() => episodes.filter((e) => e.suggestion), [episodes])
  const [selected, setSelected] = useState<Set<string>>(new Set())

  // Select everything each time the modal opens; the list it opens on is the list to review.
  useEffect(() => {
    if (open) setSelected(new Set(pending.map((e) => e.id)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const toggle = (id: string) =>
    setSelected((s) => {
      const next = new Set(s)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  const allOn = pending.length > 0 && pending.every((e) => selected.has(e.id))
  const ids = pending.filter((e) => selected.has(e.id)).map((e) => e.id)
  const busy = applySuggestions.isPending || dismissSuggestions.isPending

  // No success toast: the rows leave the list, which is the confirmation (toast policy).
  const after = { onError: (err: unknown) => notify({ type: 'error', text: (err as Error).message }) }
  const onApply = () => applySuggestions.mutate(ids, after)
  const onDismiss = () => dismissSuggestions.mutate(ids, after)

  useEffect(() => {
    if (open && !pending.length) onClose()
  }, [open, pending.length, onClose])

  return (
    <Modal
      title="Suggested solutions and industries"
      open={open}
      onClose={onClose}
      sx={{ width: 760 }}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Close
          </Button>
          <Button variant="secondary" onClick={onDismiss} disabled={busy || !ids.length}>
            Dismiss {ids.length}
          </Button>
          <Button variant="primary" onClick={onApply} disabled={busy || !ids.length}>
            Apply {ids.length}
          </Button>
        </>
      }
    >
      <Stack spacing={3}>
        <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          A model read each episode&apos;s title and description and picked from the website&apos;s solutions and industries. Applying
          fills only lists an episode does not have yet. Untick anything that is wrong.
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2 }}>
          <Checkbox
            size="small"
            checked={allOn}
            indeterminate={!allOn && ids.length > 0}
            onChange={() => setSelected(allOn ? new Set() : new Set(pending.map((e) => e.id)))}
            inputProps={{ 'aria-label': 'Select all' }}
          />
          <Typography variant="body3" color="text.secondary">
            {ids.length} of {pending.length} selected
          </Typography>
        </Stack>
        <Box sx={(theme) => ({ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, maxHeight: 440, overflowY: 'auto' })}>
          {pending.map((e, i) => {
            const s = e.suggestion!
            return (
              <Stack
                key={e.id}
                direction="row"
                spacing={2}
                alignItems="flex-start"
                onClick={() => toggle(e.id)}
                sx={(theme) => ({
                  p: 3,
                  cursor: 'pointer',
                  borderBottom: i < pending.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                  '&:hover': { backgroundColor: theme.palette.action.hover },
                })}
              >
                <Checkbox
                  size="small"
                  checked={selected.has(e.id)}
                  onChange={() => toggle(e.id)}
                  onClick={(ev) => ev.stopPropagation()}
                  inputProps={{ 'aria-label': e.title }}
                  sx={{ mt: -1 }}
                />
                <Stack spacing={1.5} sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body3" sx={{ fontWeight: 500, overflowWrap: 'anywhere' }}>
                    {e.title}
                  </Typography>
                  <Stack direction="row" useFlexGap spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                    {s.solutions.map((x) => (
                      <ToneChip key={x} tone="info" label={solutionLabel(x)} />
                    ))}
                    {s.industries.map((x) => (
                      <ToneChip key={x} tone="neutral" label={industryLabel(x)} />
                    ))}
                    {!s.solutions.length && !s.industries.length && (
                      <Typography variant="body4" color="text.secondary">
                        Nothing suggested
                      </Typography>
                    )}
                  </Stack>
                  {s.reason && (
                    <Typography variant="body4" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                      {s.reason}
                    </Typography>
                  )}
                  {(e.solutions.length > 0 || e.industries.length > 0) && (
                    <Typography variant="body4" color="text.secondary">
                      Already set, kept as is: {[...e.solutions.map(solutionLabel), ...e.industries.map(industryLabel)].join(', ')}
                    </Typography>
                  )}
                </Stack>
              </Stack>
            )
          })}
        </Box>
      </Stack>
    </Modal>
  )
}
